import React, { useCallback, useEffect, useState } from 'react';
import axios from 'axios';
import { toast } from 'sonner';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription } from '../ui/dialog';
import { Button } from '../ui/button';
import { Loader2, Wand2 } from 'lucide-react';

const API_URL = process.env.REACT_APP_BACKEND_URL;

const Stat = ({ label, value, testid }) => (
  <div className="border border-slate-200 rounded-md px-3 py-2 text-center" data-testid={testid}>
    <p className="text-lg font-bold text-[#0F2040]">{value}</p>
    <p className="text-[10px] text-slate-500">{label}</p>
  </div>
);

export const OverrideStatsDialog = ({ open, onOpenChange }) => {
  const [stats, setStats] = useState(null);
  const [boosting, setBoosting] = useState('');
  const [boosted, setBoosted] = useState({});
  const [targetInput, setTargetInput] = useState('');

  const saveTarget = async () => {
    const pct = parseFloat(targetInput);
    if (Number.isNaN(pct) || pct < 0 || pct > 100) { toast.error('Target must be a number between 0 and 100'); return; }
    try {
      await axios.post(`${API_URL}/api/rfp/response/override-target`, { pct }, { withCredentials: true });
      toast.success(`Override-rate target set to ${pct}% — the monthly trend is coloured against it`);
      setStats(prev => (prev ? { ...prev, target_pct: pct } : prev));
    } catch (err) { toast.error(err?.response?.data?.detail || 'Could not save target'); }
  };

  const rematch = async (c) => {
    setBoosting(c.block_id);
    try {
      await axios.post(`${API_URL}/api/rfp/boilerplate/${c.block_id}/boost-keywords`, {}, { withCredentials: true });
      toast.success(`${c.capability_id} keywords regenerated — re-match against open RFPs queued`);
      setBoosted(prev => ({ ...prev, [c.block_id]: true }));
    } catch (err) { toast.error(err?.response?.data?.detail || 'Re-match failed'); }
    setBoosting('');
  };

  const load = useCallback(async () => {
    try {
      const r = await axios.get(`${API_URL}/api/rfp/response/override-stats`, { withCredentials: true });
      setStats(r.data);
    } catch { setStats({ total_overrides: 0, totals: {}, direction: {}, by_rep: [], top_capabilities: [] }); }
  }, []);
  useEffect(() => { if (open) { setStats(null); load(); } }, [open, load]);

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="max-w-2xl max-h-[85vh] overflow-y-auto" data-testid="lib-overrides-dialog">
        <DialogHeader>
          <DialogTitle className="text-[#0F2040]">AI assessment overrides</DialogTitle>
          <DialogDescription className="text-xs">
            How often reps manually correct the AI's match strength, confidence and fit —
            frequently overridden capabilities are where matching needs tuning.
          </DialogDescription>
        </DialogHeader>
        {!stats && <Loader2 className="w-5 h-5 animate-spin text-[#4ABFCF]" />}
        {stats && stats.total_overrides === 0 && (
          <p className="text-sm text-slate-400 py-6 text-center" data-testid="lib-overrides-empty">
            No overrides yet — reps haven't corrected any AI assessments.
          </p>
        )}
        {stats && stats.total_overrides > 0 && (
          <div className="space-y-4">
            <div className="grid grid-cols-4 gap-2">
              <Stat label={`of ${stats.total_questions} questions`} value={`${stats.override_rate_pct}%`} testid="lib-overrides-rate" />
              <Stat label="strength" value={stats.totals.strength || 0} testid="lib-overrides-strength" />
              <Stat label="confidence" value={stats.totals.confidence || 0} testid="lib-overrides-confidence" />
              <Stat label="fit" value={stats.totals.fit || 0} testid="lib-overrides-fit" />
            </div>
            <p className="text-[11px] text-slate-500" data-testid="lib-overrides-direction">
              Strength changes: <span className="font-semibold text-emerald-700">{stats.direction.upgraded || 0} upgraded</span>
              {' · '}<span className="font-semibold text-rose-600">{stats.direction.downgraded || 0} downgraded</span>
              {' '}— many upgrades mean the AI under-scores matches; many downgrades mean it over-promises.
            </p>
            {stats.months?.length > 0 && (
              <div>
                <div className="flex items-center gap-2 mb-1">
                  <p className="text-[10px] font-semibold uppercase text-slate-400"
                    title="Current standing overrides by the month they were made — reverted overrides drop out">
                    Trend by month
                  </p>
                  <span className="flex-1" />
                  {stats.target_pct != null && (
                    <span className="text-[10px] text-slate-500" data-testid="lib-overrides-target-label">
                      target ≤ {stats.target_pct}%
                    </span>
                  )}
                  {stats.target_history?.length > 0 && (
                    <span className="text-[9px] text-slate-400 cursor-help underline decoration-dotted"
                      data-testid="lib-overrides-target-history"
                      title={stats.target_history.map(h => `${h.pct}% set by ${h.by} on ${(h.at || '').slice(0, 10)}`).join('\n')}>
                      {stats.target_history.length} change{stats.target_history.length === 1 ? '' : 's'}
                    </span>
                  )}
                  <input type="number" min="0" max="100" step="0.1" value={targetInput}
                    onChange={e => setTargetInput(e.target.value)}
                    onKeyDown={e => { if (e.key === 'Enter') saveTarget(); }}
                    placeholder={stats.target_pct != null ? `${stats.target_pct}` : 'target %'}
                    className="h-6 w-16 text-[10px] border border-slate-200 rounded px-1.5 focus:outline-none focus:border-[#4ABFCF]"
                    data-testid="lib-overrides-target-input" />
                  <button type="button" onClick={saveTarget}
                    className="text-[10px] font-semibold text-[#4ABFCF] hover:underline"
                    data-testid="lib-overrides-target-save">Set target</button>
                </div>
                <div className="space-y-1" data-testid="lib-overrides-months">
                  {stats.months.map(m => {
                    const max = Math.max(...stats.months.map(x => x.total), 1);
                    const onTarget = stats.target_pct == null ? null : m.rate_pct <= stats.target_pct;
                    return (
                      <div key={m.month} className="flex items-center gap-2 text-[11px]"
                        data-testid={`lib-overrides-month-${m.month}`}>
                        <span className="w-16 shrink-0 text-slate-500">{m.month}</span>
                        <div className="flex-1 h-2.5 bg-slate-100 rounded overflow-hidden">
                          <div className={`h-full ${onTarget === null ? 'bg-[#4ABFCF]' : onTarget ? 'bg-emerald-500' : 'bg-rose-500'}`}
                            style={{ width: `${(m.total / max) * 100}%` }} />
                        </div>
                        <span className={`w-44 shrink-0 text-[10px] ${onTarget === false ? 'text-rose-600 font-semibold' : 'text-slate-500'}`}>
                          {m.total} · {m.rate_pct}% ({m.strength}s · {m.confidence}c · {m.fit}f)
                        </span>
                      </div>
                    );
                  })}
                </div>
              </div>
            )}
            <div>
              <p className="text-[10px] font-semibold uppercase text-slate-400 mb-1">By rep</p>
              <div className="space-y-1" data-testid="lib-overrides-reps">
                {stats.by_rep.map(r => (
                  <div key={r.rep} className="flex items-center gap-2 text-[11px]">
                    <span className="w-56 truncate text-slate-600">{r.rep}</span>
                    <span className="font-semibold text-[#0F2040]">{r.total}</span>
                    <span className="text-slate-400">({r.strength} strength · {r.confidence} confidence · {r.fit} fit)</span>
                  </div>
                ))}
              </div>
            </div>
            {stats.top_capabilities.length > 0 && (
              <div>
                <p className="text-[10px] font-semibold uppercase text-slate-400 mb-1">Most-corrected capabilities (strength)</p>
                <div className="space-y-1" data-testid="lib-overrides-caps">
                  {stats.top_capabilities.map(c => (
                    <div key={c.capability_id} className="flex items-center gap-2 text-[11px]">
                      <span className="font-semibold text-[#0F2040] w-24 shrink-0">{c.capability_id}</span>
                      <span className="flex-1 truncate text-slate-600">{c.title}</span>
                      <span className="text-slate-500">{c.count}×</span>
                      {c.block_id && (
                        <span className="text-[10px] text-slate-400" data-testid={`lib-overrides-matches-${c.capability_id}`}
                          title={c.boost
                            ? `Matched to ${c.matches} question(s). At the last re-match (${(c.boost.at || '').slice(0, 10)}) it was ${c.boost.baseline_matches} — ${c.boost.gained > 0 ? `picked up ${c.boost.gained} new` : c.boost.gained < 0 ? `${Math.abs(c.boost.gained)} fewer` : 'no change yet (new matches appear after projects re-run matching)'}`
                            : `Matched to ${c.matches} question(s) across all RFPs`}>
                          {c.matches} matches
                          {c.boost && (
                            <span className={`ml-1 font-semibold ${c.boost.gained > 0 ? 'text-emerald-600' : c.boost.gained < 0 ? 'text-rose-500' : 'text-slate-400'}`}>
                              {c.boost.gained > 0 ? `+${c.boost.gained}` : c.boost.gained}
                            </span>
                          )}
                        </span>
                      )}
                      {c.block_id && (
                        <Button size="sm" variant="outline" disabled={boosting === c.block_id || boosted[c.block_id]}
                          title="AI regenerates this block's keywords and queues a re-match against open RFPs"
                          onClick={() => rematch(c)}
                          className="h-5 text-[9px] px-1.5 text-[#0F2040] border-slate-200 hover:bg-slate-50"
                          data-testid={`lib-overrides-rematch-${c.capability_id}`}>
                          {boosting === c.block_id ? <Loader2 className="w-3 h-3 animate-spin" />
                            : boosted[c.block_id] ? 'Queued ✓'
                              : <><Wand2 className="w-2.5 h-2.5 mr-1" />Re-match</>}
                        </Button>
                      )}
                    </div>
                  ))}
                </div>
              </div>
            )}
          </div>
        )}
      </DialogContent>
    </Dialog>
  );
};
